import { useState, type ReactNode } from "react";
import { cn } from "../lib/utils.js";

/**
 * A click-to-expand section. Two modes:
 *   - default: renders its own card chrome (border + white bg + a mist header
 *     bar). Use when the children are bare content (e.g. a table).
 *   - bare: renders just a standalone toggle button; the children render below
 *     it untouched. Use when the children are ALREADY a self-contained <Card>
 *     (so we don't nest a card inside a card).
 *
 * `defaultOpen` only sets the initial state — the user can always toggle. The
 * hook lives inside this component, so a parent can pass a data-derived default
 * (e.g. defaultOpen={!enrolled}) without tripping the rules-of-hooks ordering.
 */
export function Collapsible({
  title,
  subtitle,
  defaultOpen = false,
  bare = false,
  className,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  defaultOpen?: boolean;
  bare?: boolean;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const chevron = (
    <svg
      className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
  const titleBlock = (
    <div>
      <div className="text-sm font-bold text-simuflex-navy">{title}</div>
      {subtitle ? <div className="mt-0.5 text-xs text-slate-500">{subtitle}</div> : null}
    </div>
  );

  if (bare) {
    return (
      <div className={cn("space-y-2", className)}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-3 rounded-xl border border-simuflex-line bg-white px-5 py-3 text-left shadow-sm transition-colors hover:bg-simuflex-mist/30"
        >
          {titleBlock}
          {chevron}
        </button>
        {open ? children : null}
      </div>
    );
  }

  return (
    <div
      className={cn("overflow-hidden rounded-2xl border border-simuflex-line bg-white", className)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 bg-simuflex-mist/20 px-6 py-4 text-left transition-colors hover:bg-simuflex-mist/40"
      >
        {titleBlock}
        {chevron}
      </button>
      {open ? children : null}
    </div>
  );
}
